import { LinkIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";

import ChooseSourceDialog, { ChosenSource } from "./ChooseSourceDialog";
import SourceEmptyState from "./SourceEmptyState";
import SourceSummaryRows from "./SourceSummaryRows";
import { CompositionConfiguration } from "./types";
import resolveSourcePreview from "./utils/resolveSourcePreview";
import {
  applyChosenSources,
  hasAnyConfiguredSources,
  removeConfiguredSourceAtIndex,
} from "./utils/sourceConfiguration";

import { BlockProps } from "@/components/PowerBlock/Block";
import { useBlock, withBlockStore } from "@/components/PowerBlock/Block/block-store";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import { useListAvailableSources } from "@/queries/smart-tables/useListAvailableSources";
import { useColumnConfigStore } from "@/routes/smart-table/column-config-context";
import { useColumnTargetContext } from "@/routes/smart-table/components/SmartTablePage/Sheet/ColumnConfigSidebar/column-target-context";
import { useSheetDataStoreSafe } from "@/routes/smart-table/sheet-data-store-context";

const validationSchema = createBlockSchema<CompositionConfiguration>()
  .requireName()
  .addRule({
    path: "configuration.sources",
    validate: (_value, fullPayload) => hasAnyConfiguredSources(fullPayload.configuration ?? {}),
    message: "Pick a source column to pull values from",
  })
  .build();

const CompositionBlockCore = observer(
  (_props: Omit<BlockProps<CompositionConfiguration>, "DefaultFields">) => {
    const block = useBlock<CompositionConfiguration>();
    const isReadonly = useIsReadonly();
    const { tableId: currentTableId, sheetId: currentSheetId, columnId } = useColumnTargetContext();
    const sheetDataStore = useSheetDataStoreSafe();
    const columnConfigStore = useColumnConfigStore();

    const configuration = useMemo(
      () => block.configuration ?? ({} as CompositionConfiguration),
      [block.configuration],
    );

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogInitialResolver, setDialogInitialResolver] = useState<string | undefined>(
      undefined,
    );

    const openDialog = (resolver?: string) => {
      if (isReadonly) {
        return;
      }
      setDialogInitialResolver(resolver);
      setDialogOpen(true);
    };

    const { data: compositionGroups = [], isLoading: isLoadingSources } = useListAvailableSources({
      excludeSheetId: currentSheetId,
      outputOnly: false,
    });

    const preview = useMemo(
      () =>
        resolveSourcePreview({
          configuration,
          compositionGroups,
          currentTableId,
          isLoadingSources,
        }),
      [compositionGroups, configuration, currentTableId, isLoadingSources],
    );

    const hasAnyAvailableSources = compositionGroups.length > 0;
    const hasConfiguredSources = hasAnyConfiguredSources(configuration);

    const handleSourceChosen = (chosen: ChosenSource | ChosenSource[]) => {
      const nextConfiguration = applyChosenSources(configuration, chosen);
      block.setConfiguration(nextConfiguration);

      if (!columnId) {
        return;
      }

      columnConfigStore.setColumnConfig(columnId, {
        configuration: nextConfiguration as Record<string, unknown>,
      });

      sheetDataStore?.markColumnCellsStale(columnId);
    };

    const handleRemoveSource = (indexToRemove: number) => {
      const nextConfiguration = removeConfiguredSourceAtIndex(configuration, indexToRemove);
      block.setConfiguration(nextConfiguration);

      if (!columnId) {
        return;
      }

      columnConfigStore.setColumnConfig(columnId, {
        configuration: nextConfiguration as Record<string, unknown>,
      });

      sheetDataStore?.markColumnCellsStale(columnId);
    };

    const sectionDescription = hasConfiguredSources
      ? undefined
      : "Each row here will show the value from a column you pick.";

    return (
      <>
        <SectionGroup
          title="Copy from another column"
          description={sectionDescription}
          icon={<LinkIcon className="h-4 w-4" />}
        >
          {!hasConfiguredSources && (
            <SourceEmptyState
              isLoadingSources={isLoadingSources && compositionGroups.length === 0}
              hasAnyAvailableSources={hasAnyAvailableSources}
              isReadonly={isReadonly}
              onChooseSource={openDialog}
            />
          )}

          {hasConfiguredSources && preview && (
            <SourceSummaryRows
              summaries={preview}
              isReadonly={isReadonly}
              onOpenDialog={openDialog}
              onRemoveSource={handleRemoveSource}
            />
          )}
        </SectionGroup>

        <ChooseSourceDialog
          open={dialogOpen}
          onOpenChange={nextOpen => {
            setDialogOpen(nextOpen);
            if (!nextOpen) {
              setDialogInitialResolver(undefined);
            }
          }}
          initialResolver={dialogInitialResolver}
          compositionGroups={compositionGroups}
          currentTableId={currentTableId}
          currentColumnId={columnId}
          value={configuration.source}
          values={configuration.sources}
          fallbackTableTitle={configuration.smart_table_title}
          fallbackSheetTitle={configuration.sheet_title}
          onSelect={handleSourceChosen}
        />
      </>
    );
  },
);

export default withBlockStore<CompositionConfiguration>(CompositionBlockCore, {
  validationSchema,
});
